import { CloseCode } from "@obean/protocol";
import type { InviteState } from "./api.js";
import type { ConnectionStatus } from "./room-connection.js";

/** join_failed's `stage` (docs/metrics.md). */
export type JoinStage = "link" | "nickname" | "token" | "ws_connect" | "room_full" | "timeout";

/** An invite link's secret, from the address's fragment (`/r/:id#secret`); null if there's none. */
export function secretFromHash(hash: string): string | null {
  const secret = hash.replace(/^#/, "");
  return /^[A-Za-z0-9_-]{16,64}$/.test(secret) ? secret : null;
}

/** What to tell someone whose invite link can't be used, and why (for join_failed). */
export function inviteProblem(
  state: InviteState | "not_found",
  inviter: string | null,
): { message: string; code: string } {
  const who = inviter ?? "your friend";
  switch (state) {
    case "expired":
      return { message: `This invite has expired. Ask ${who} for a new link.`, code: "expired" };
    case "revoked":
      return { message: `This invite was withdrawn. Ask ${who} for a new link.`, code: "revoked" };
    case "room_closed":
      return { message: "This room has closed.", code: "room_closed" };
    case "not_found":
      return {
        message: "This invite link doesn't work. Check you copied all of it.",
        code: "not_found",
      };
    case "valid":
      return { message: "", code: "valid" };
  }
}

/**
 * The join_failed a closed connection means, if the client should report one. A full room is
 * reported by the room itself; a left room isn't a failure.
 */
export function joinFailure(
  status: Extract<ConnectionStatus, { kind: "closed" }>,
): { stage: JoinStage; code: string } | null {
  if (status.code === 0) return { stage: "token", code: status.reason };
  if (status.code === CloseCode.roomFull || status.code === CloseCode.normal) return null;
  if (status.code === CloseCode.roomClosed) return { stage: "link", code: "room_closed" };
  return { stage: "ws_connect", code: String(status.code) };
}
